/**
 * RestorePivotDialog — spec iter164G/K (2026-02-27).
 *
 * Modale dédiée à la restauration ciblée « état 23/08/2026 » pour :
 *   - Acte de vente VSP
 *   - Acte de vente final
 * Exclut : DC0222/26 et DC0223/26.
 *
 * Flow :
 *   1) Ouverture → GET /admin/restore-to-pivot-date/preview (read-only)
 *   2) Affichage du texte dans un <pre> monospace lisible
 *   3) Bouton "Confirmer et appliquer" DÉBLOQUÉ uniquement quand
 *      l'utilisateur a saisi la phrase EXACTE
 *      "CONFIRMER RESTAURATION 23/08" dans l'input dédié.
 *   4) Clic sur Apply → POST /admin/restore-to-pivot-date/apply
 *   5) Résultat (docs_modified, steps_removed, backup_collection) affiché
 *      dans la même fenêtre.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AlertTriangle, CheckCircle2, Loader2, RefreshCw, ShieldCheck } from "lucide-react";
import api from "@/lib/api";
import { toast } from "@/lib/toast";

const PARAMS = {
  type_names:   "Acte de vente VSP,Acte de vente final",
  pivot_date:   "2026-08-23T23:59:59Z",
  exclude_refs: "DC0222/26,DC0223/26",
};
const CONFIRMATION_PHRASE = "CONFIRMER RESTAURATION 23/08";

export default function RestorePivotDialog({ open, onOpenChange }) {
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState("");
  const [error, setError] = useState(null);

  // Apply flow
  const [confirmInput, setConfirmInput] = useState("");
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);   // audit payload
  const canApply = confirmInput.trim() === CONFIRMATION_PHRASE && !applying && !result && !loading;

  const loadPreview = useCallback(async () => {
    setLoading(true);
    setError(null);
    setPreview("");
    try {
      const { data } = await api.get(
        "/document-types/admin/restore-to-pivot-date/preview",
        {
          params: PARAMS,
          transformResponse: [(v) => (typeof v === "string" ? v : String(v ?? ""))],
          responseType: "text",
        }
      );
      setPreview(typeof data === "string" ? data : String(data ?? ""));
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.response?.data || err?.message || "Erreur inconnue";
      setError(typeof msg === "string" ? msg : JSON.stringify(msg));
    } finally {
      setLoading(false);
    }
  }, []);

  const applyRestore = useCallback(async () => {
    if (!canApply) return;
    setApplying(true);
    try {
      const { data } = await api.post(
        "/document-types/admin/restore-to-pivot-date/apply",
        { ...PARAMS, confirmation_phrase: confirmInput.trim() },
      );
      setResult(data);
      toast.success("Restauration appliquée", {
        description: `${data.docs_modified} doc(s), ${data.steps_removed} étape(s) retirée(s). Backup : ${data.backup_collection}`,
      });
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Erreur inconnue";
      toast.error("Échec de l'application", { description: msg });
      setError(msg);
    } finally {
      setApplying(false);
    }
  }, [canApply, confirmInput]);

  useEffect(() => {
    if (open) {
      setConfirmInput("");
      setResult(null);
      setError(null);
      loadPreview();
    }
  }, [open, loadPreview]);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!applying) onOpenChange?.(v); }}>
      <DialogContent
        className="max-w-4xl max-h-[92vh] overflow-hidden flex flex-col"
        style={{ background: "#161616", border: "1.5px solid #EA580C" }}
        data-testid="restore-pivot-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <ShieldCheck size={16} style={{ color: "#FB923C" }} />
            Restauration au 23/08/2026 · VSP + Final
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 flex-1 overflow-y-auto pr-1">
          {/* Bandeau paramètres */}
          <div
            className="rounded-md px-3 py-2 text-[11px] flex items-start gap-2"
            style={{ background: "rgba(234,88,12,0.10)", border: "1px solid rgba(234,88,12,0.35)" }}
          >
            <AlertTriangle size={12} className="mt-[2px]" style={{ color: "#FB923C" }} />
            <div style={{ color: "#B7B7B7", lineHeight: 1.5 }}>
              Aperçu de la restauration ciblée aux paramètres fixés :
              <div className="mt-1 pl-2 space-y-0.5" style={{ color: "#EDEDED", fontFamily: "monospace" }}>
                <div>• Types &nbsp;&nbsp;&nbsp;: Acte de vente VSP, Acte de vente final</div>
                <div>• Pivot &nbsp;&nbsp;&nbsp;: 2026-08-23T23:59:59Z (fin de journée)</div>
                <div>• Exclus &nbsp;: DC0222/26, DC0223/26</div>
              </div>
            </div>
          </div>

          {loading && (
            <div className="flex items-center gap-2 justify-center py-8 text-sm text-muted-foreground">
              <Loader2 size={14} className="animate-spin" /> Chargement de l'aperçu…
            </div>
          )}

          {error && (
            <div
              className="rounded-md px-3 py-2 text-xs"
              style={{ background: "rgba(220,38,38,0.15)", border: "1px solid rgba(220,38,38,0.45)", color: "#F87171" }}
              data-testid="restore-pivot-error"
            >
              <b>Erreur :</b> {error}
            </div>
          )}

          {!loading && !error && preview && !result && (
            <pre
              className="rounded-md text-[11.5px] p-3 overflow-x-auto whitespace-pre"
              style={{
                background: "#0F0F0F", border: "1px solid #2C2C2C",
                color: "#E5E5E5", fontFamily: "'Consolas','Monaco',monospace",
                lineHeight: 1.5, maxHeight: "45vh", overflowY: "auto",
              }}
              data-testid="restore-pivot-preview"
            >
              {preview}
            </pre>
          )}

          {/* Bloc résultat post-apply */}
          {result && (
            <div
              className="rounded-md p-4 space-y-2"
              style={{
                background: "rgba(34,197,94,0.10)",
                border: "1.5px solid rgba(34,197,94,0.55)",
              }}
              data-testid="restore-pivot-result"
            >
              <div className="flex items-center gap-2 text-sm font-bold" style={{ color: "#4ADE80" }}>
                <CheckCircle2 size={18} /> Restauration appliquée avec succès
              </div>
              <div className="grid grid-cols-4 gap-3 text-xs">
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Documents modifiés</div>
                  <div className="text-lg font-bold" style={{ color: "#EDEDED" }}>{result.docs_modified}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Docs uniquement protégés</div>
                  <div className="text-lg font-bold" style={{ color: "#EDEDED" }}>{result.docs_only_protected ?? 0}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Étapes retirées</div>
                  <div className="text-lg font-bold" style={{ color: "#EDEDED" }}>{result.steps_removed}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Étapes validées protégées</div>
                  <div className="text-lg font-bold" style={{ color: "#EDEDED" }}>{result.steps_protected}</div>
                </div>
              </div>
              <div className="text-[10px] italic" style={{ color: "#9CA3AF" }}>
                Docs uniquement protégés = documents impactés mais n'ayant subi aucune mutation
                (toutes leurs étapes post-pivot étaient déjà validées).
              </div>
              <div className="text-[11px] pt-2" style={{ color: "#B7B7B7" }}>
                Backup créé dans la collection :&nbsp;
                <code className="px-1.5 py-0.5 rounded" style={{ background: "#0F0F0F", color: "#4ADE80" }}>
                  {result.backup_collection}
                </code>
              </div>
              <div className="text-[10px]" style={{ color: "#8A8A8F" }}>
                Rollback possible via l'endpoint dédié en fournissant le nom de la collection ci-dessus.
              </div>
              {result.per_type && (
                <div className="pt-2 space-y-0.5 text-[11px]">
                  {result.per_type.map((p, i) => (
                    <div key={i} className="flex justify-between" style={{ color: "#B7B7B7" }}>
                      <span>{p.type_name}</span>
                      <span style={{ color: "#EDEDED" }}>
                        {p.docs_modified} docs modifiés
                        {p.docs_only_protected ? ` · ${p.docs_only_protected} uniquement protégés` : ""}
                        {` · ${p.steps_removed} retirées`}
                        {p.steps_protected ? ` · ${p.steps_protected} protégées` : ""}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Zone de confirmation par phrase — visible uniquement si preview OK et pas de résultat */}
          {!loading && !error && preview && !result && (
            <div
              className="rounded-md px-3 py-3 space-y-2"
              style={{ background: "#0F0F0F", border: "1.5px solid #DC2626" }}
              data-testid="restore-pivot-confirm-block"
            >
              <div className="text-[11px] font-semibold" style={{ color: "#F87171" }}>
                Pour débloquer le bouton d'application, saisissez EXACTEMENT :
              </div>
              <div className="text-[13px] font-mono px-2 py-1 rounded" style={{ background: "#1A1A1A", color: "#FB923C", letterSpacing: 1 }}>
                {CONFIRMATION_PHRASE}
              </div>
              <Input
                value={confirmInput}
                onChange={(e) => setConfirmInput(e.target.value)}
                placeholder="Saisir la phrase ci-dessus (majuscules obligatoires)"
                disabled={applying}
                className="font-mono text-xs"
                style={{
                  background: "#161616",
                  border: `1.5px solid ${confirmInput.trim() === CONFIRMATION_PHRASE ? "#4ADE80" : "#3F3F46"}`,
                }}
                data-testid="restore-pivot-confirm-input"
              />
              {confirmInput.trim() && confirmInput.trim() !== CONFIRMATION_PHRASE && (
                <div className="text-[10px]" style={{ color: "#F87171" }}>
                  ✗ Phrase incorrecte — l'application reste bloquée.
                </div>
              )}
              {confirmInput.trim() === CONFIRMATION_PHRASE && (
                <div className="text-[10px]" style={{ color: "#4ADE80" }}>
                  ✓ Phrase valide — le bouton « Confirmer et appliquer » est débloqué.
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          {!result && (
            <Button
              variant="outline"
              onClick={loadPreview}
              disabled={loading || applying}
              data-testid="restore-pivot-refresh"
            >
              <RefreshCw size={12} className={loading ? "animate-spin mr-1.5" : "mr-1.5"} />
              Rafraîchir l'aperçu
            </Button>
          )}
          <div className="flex-1" />
          <Button
            variant="outline"
            onClick={() => onOpenChange?.(false)}
            disabled={applying}
            data-testid="restore-pivot-close"
          >
            {result ? "Fermer" : "Annuler"}
          </Button>
          {!result && (
            <Button
              disabled={!canApply}
              onClick={applyRestore}
              data-testid="restore-pivot-apply-btn"
              style={{
                background: canApply ? "#DC2626" : "#4B5563",
                color: "white",
                cursor: canApply ? "pointer" : "not-allowed",
              }}
              title={canApply ? "Lancer la restauration" : "Saisir la phrase de confirmation pour débloquer"}
            >
              {applying ? (
                <span className="inline-flex items-center gap-1.5">
                  <Loader2 size={13} className="animate-spin" /> Restauration en cours…
                </span>
              ) : "Confirmer et appliquer"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
